import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import API_BASE from "../config/api";
import Swal from "sweetalert2";
import VoiceSearchButton from "./VoiceSearchButton";

export default function HeroSection() {
  const navigate = useNavigate();

  const [provinces, setProvinces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [selectedProvince, setSelectedProvince] = useState("Province");
  const [selectedCategory, setSelectedCategory] = useState("Categories");
  const [categoryFilter, setCategoryFilter] = useState("");
  const filteredCategories = categories.filter(item =>
    item.catagories
      .toLowerCase()
      .includes(categoryFilter.toLowerCase())
  );

  // ---------- Search autocomplete (categories + businesses) ----------
  const searchBoxRef = useRef(null);
  const [suggestions, setSuggestions] = useState({ categories: [], businesses: [] });
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Flat list (categories first, then businesses) for keyboard navigation
  const flatItems = useMemo(() => ([
    ...suggestions.categories.map((c) => ({ kind: "category", data: c })),
    ...suggestions.businesses.map((b) => ({ kind: "business", data: b })),
  ]), [suggestions]);

  // Debounced fetch as the user types
  useEffect(() => {
    const q = searchText.trim();
    if (q.length < 2) {
      setSuggestions({ categories: [], businesses: [] });
      setShowSuggest(false);
      setSuggestLoading(false);
      return;
    }

    setSuggestLoading(true);
    const timer = setTimeout(() => {
      fetch(`${API_BASE}/api/searchSuggestions.php?q=${encodeURIComponent(q)}`)
        .then((res) => res.json())
        .then((data) => {
          if (data && data.success) {
            setSuggestions({
              categories: data.categories || [],
              businesses: data.businesses || [],
            });
            setShowSuggest(true);
            setActiveIndex(-1);
          }
        })
        .catch(() => { /* network errors are non-fatal for typeahead */ })
        .finally(() => setSuggestLoading(false));
    }, 250);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Close the dropdown when clicking outside the search box
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setShowSuggest(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------- Fire-and-forget search logging (Trending Searches Near You) ----------
  const logSearch = ({ term = "", category = "", province = "" }) => {
    try {
      if (province) localStorage.setItem("dn_province", province);
      if (!term && !category) return;
      fetch(`${API_BASE}/api/log_search.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ term, category, province }),
        keepalive: true,
      }).catch(() => { });
    } catch (e) { /* non-fatal */ }
  };

  const handleSelectCategory = (label) => {
    setShowSuggest(false);
    setActiveIndex(-1);
    setSearchText(label);
    const province = selectedProvince === "Province" ? "" : selectedProvince;
    logSearch({ category: label, province });
    navigate(
      `/search?search=&location=${encodeURIComponent(province)}&category=${encodeURIComponent(label)}`
    );
  };

  const handleSelectBusiness = (biz) => {
    setShowSuggest(false);
    setActiveIndex(-1);
    setSearchText(biz.title);
    logSearch({ term: biz.title });
    navigate(
      `/search?search=${encodeURIComponent(biz.title)}&location=&category=`
    );
  };

  const onInputKeyDown = (e) => {
    if (!showSuggest || flatItems.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault();
        goToSearch();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? flatItems.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < flatItems.length) {
        const item = flatItems[activeIndex];
        if (item.kind === "category") handleSelectCategory(item.data.label);
        else handleSelectBusiness(item.data);
      } else {
        goToSearch();
      }
    } else if (e.key === "Escape") {
      setShowSuggest(false);
      setActiveIndex(-1);
    }
  };

  const showToast = (message, icon = "warning") => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon,
      title: message,
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      customClass: {
        popup: "mini-toast",
      },
    });
  };

  useEffect(() => {
    fetch(`${API_BASE}/api/getProvinces.php`)
      .then(res => res.json())
      .then(data => setProvinces(data.provinces || []));

    fetch(`${API_BASE}/api/getCategories.php`)
      .then(res => res.json())
      .then(data => setCategories(data.categories || []));
  }, []);

  const canSearch = () => {
    const hasText = searchText.trim().length > 0;
    const hasProvince = selectedProvince !== "Province";
    const hasCategory = selectedCategory !== "Categories";

    return hasText || hasProvince || hasCategory;
  };

  const goToSearch = () => {
    if (!canSearch()) {
      showToast("Please enter a keyword or select a province/category", "warning");
      return;
    }

    const province = selectedProvince === "Province" ? "" : selectedProvince;
    const category = selectedCategory === "Categories" ? "" : selectedCategory;
    const search = searchText.trim();

    logSearch({ term: search, category, province });

    navigate(
      `/search?search=${encodeURIComponent(search)}&location=${encodeURIComponent(
        province
      )}&category=${encodeURIComponent(category)}`
    );
  };

  return (
    <>
      {/* Internal CSS */}
      <style>{`
        :root {
          --brand-primary: #1E49FF;
          --headline: #0B1220;
          --muted: #6B7280;
          --pill-radius: 8px;
          --bar-height: 50px;
        }

        .hero-wrap {
          padding: 40px 0 24px;
          text-align: center;
        }

        .hero-title {
          font-size: 48px;
          font-weight: 700;
          color: var(--headline);
        }

        @media (max-width: 991px) {
          .hero-title { font-size: 40px; }
        }

        @media (max-width: 575px) {
          .hero-title { font-size: 32px; }
        }
      `}</style>
      <style>{`
        /* Animated colorful border for search card */
      .search-shell {
        position: relative;
        border: 1px solid #959ca3ff;  
        border-radius: 10px !important;
        padding: 1px; /* border thickness */
        background-size: 400% 400%;
      }

      /* Inner white card (actual content) */
      .search-shell::before {
        content: "";
        position: absolute;
        inset: 4px; /* SAME AS padding */
        background: #ffffff;
        border-radius: 10px; /* slightly smaller than parent */
        z-index: 0;
      }

      /* Bring content above white layer */
      .search-shell > * {
        position: relative;
        z-index: 2;
      }

    .dropdown-menu {
    position: absolute;
    z-index: 5000 !important;
}
    .fb{
backgroundColor: "#0076d7";   // faint blue
color: #fff !important;
 }
      `}</style>

           <style>{`
        .dn-suggest {
          position: absolute; top: calc(100% + 6px); left: 0;
          width: 100%; min-width: 340px; max-width: 92vw;
          background: #fff; border: 1px solid #e3e6ea; border-radius: 10px;
          z-index: 6000; max-height: 440px; overflow-y: auto; padding: 6px 0; text-align: left;
        }
        .dn-suggest-head {
          font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
          color: #9aa1a9; padding: 8px 16px 4px;
        }
        .dn-suggest-item {
          display: flex; align-items: center; gap: 12px; width: 100%; border: 0;
          background: transparent; padding: 8px 16px; text-align: left; cursor: pointer;
          white-space: nowrap; overflow: hidden;
        }
        .dn-suggest-item:hover, .dn-suggest-item.active { background: #f3f6ff; }
        .dn-suggest-icon {
          color: #8a9099; font-size: 16px; width: 34px; height: 34px;
          display: flex; align-items: center; justify-content: center;
          background: #f1f3f5; border-radius: 8px; flex: 0 0 34px;
        }
        .dn-suggest-thumb {
          width: 34px; height: 34px; border-radius: 8px; object-fit: cover;
          flex: 0 0 34px; background: #f1f3f5;
        }
        .dn-suggest-label {
          font-size: 14px; color: #0B1220; font-weight: 500;
          flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .dn-suggest-tag { margin-left: auto; font-size: 12px; color: #9aa1a9; white-space: nowrap; flex: 0 0 auto; }
        .dn-suggest-biz { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
        .dn-suggest-sub {
          font-size: 12px; color: #6B7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .dn-suggest-rating { color: #e6a700; font-weight: 600; }
        .dn-suggest-empty { padding: 14px 16px; font-size: 14px; }
      `}</style>

      <section className="hero-wrap container">

        {/* Search Bar */}
        <div className="search-shell mt-1 p-2 shadow-sm bg-white rounded" style={{ maxWidth: "1300px", margin: "0 auto" }}>

          {/* FIXED FULL RESPONSIVE LAYOUT */}
          <div className="d-flex flex-column flex-lg-row align-items-stretch gap-2 w-100">

                       {/* SEARCH INPUT + AUTOCOMPLETE */}
            <div className="input-group flex-grow-1 position-relative" ref={searchBoxRef}>
              <span className="input-group-text bg-white border-0">
                <i className="bi bi-search fs-5 text-secondary"></i>
              </span>

              <input
                type="text"
                placeholder="What are you looking?"
                className="form-control border-0 shadow-none"
                value={searchText}
                autoComplete="off"
                onChange={(e) => setSearchText(e.target.value)}
                onFocus={() => { if (flatItems.length > 0) setShowSuggest(true); }}
                onKeyDown={onInputKeyDown}
                />

                <span className="input-group-text bg-white border-0">
                  <VoiceSearchButton onResult={(text) => setSearchText(text)} />
                </span>

              {/* AUTOCOMPLETE DROPDOWN */}

              {showSuggest && searchText.trim().length >= 2 && (
                <div className="dn-suggest shadow">

                  {suggestLoading && flatItems.length === 0 && (
                    <div className="dn-suggest-empty text-muted">
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Searching…
                    </div>
                  )}

                  {/* CATEGORIES */}
                  {suggestions.categories.length > 0 && (
                    <>
                      <div className="dn-suggest-head">Categories</div>
                      {suggestions.categories.map((c, idx) => (
                        <button
                          type="button"
                          key={`cat-${c.id}`}
                          className={`dn-suggest-item ${activeIndex === idx ? "active" : ""}`}
                          onMouseEnter={() => setActiveIndex(idx)}
                          onClick={() => handleSelectCategory(c.label)}
                        >
                          <i className="bi bi-search dn-suggest-icon"></i>
                          <span className="dn-suggest-label">{c.label}</span>
                          <span className="dn-suggest-tag">Category</span>
                        </button>
                      ))}
                    </>
                  )}

                  {/* BUSINESSES */}
                  {suggestions.businesses.length > 0 && (
                    <>
                      <div className="dn-suggest-head">Businesses</div>
                      {suggestions.businesses.map((b, idx) => {
                        const flatIdx = suggestions.categories.length + idx;
                        return (
                          <button
                            type="button"
                            key={`biz-${b.id}`}
                            className={`dn-suggest-item ${activeIndex === flatIdx ? "active" : ""}`}
                            onMouseEnter={() => setActiveIndex(flatIdx)}
                            onClick={() => handleSelectBusiness(b)}
                          >
                            <img
                              className="dn-suggest-thumb"
                              src={b.profilepics && b.profilepics.trim() !== "" ? b.profilepics : "/assets/defaultList.jpg"}
                              alt={b.title}
                              onError={(e) => { e.target.onerror = null; e.target.src = "/assets/defaultList.jpg"; }}
                            />
                            <span className="dn-suggest-biz">
                              <span className="dn-suggest-label">{b.title}</span>
                              <span className="dn-suggest-sub">
                                {b.rating ? (
                                  <>
                                    <span className="dn-suggest-rating">{b.rating.toFixed(1)} ★</span>
                                    {b.review_count ? ` (${b.review_count})` : ""}
                                    {(b.category || b.area) ? " · " : ""}
                                  </>
                                ) : null}
                                {b.category || ""}{b.category && b.area ? " · " : ""}{b.area || ""}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </>
                  )}

                  {/* NO RESULTS */}
                  {!suggestLoading && flatItems.length === 0 && (
                    <div className="dn-suggest-empty text-muted">No matches found</div>
                  )}
                </div>
              )}
            </div>

            {/* PROVINCE DROPDOWN */}
            <div className="dropdown w-100 w-lg-auto">
              <button className="btn bg-white border rounded w-100 d-flex justify-content-between align-items-center"
                data-bs-toggle="dropdown"
              >
                <span><i className="bi bi-geo-alt fs-5 me-1"></i> {selectedProvince}</span>
                <i className="bi bi-chevron-down"></i>
              </button>

              <ul className="dropdown-menu w-100" onKeyDown={(e) => e.key === "Enter" && goToSearch()}>
                {provinces.length > 0 ? (
                  provinces.map((item) => (
                    <li key={item.id}>
                      <button className="dropdown-item" onClick={() => setSelectedProvince(item.provinceName)}>
                        {item.provinceName}
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="dropdown-item text-muted">Loading...</li>
                )}
              </ul>
            </div>

            {/* CATEGORY DROPDOWN */}
            <div className="dropdown w-100 w-lg-auto">
              <button
                className="btn bg-white border rounded w-100 d-flex justify-content-between align-items-center text-truncate"
                data-bs-toggle="dropdown"
              >
                <span>
                  <i className="bi bi-grid-3x3-gap fs-5 me-1"></i>
                  {selectedCategory}
                </span>
                <i className="bi bi-chevron-down"></i>
              </button>

              <ul
                className="dropdown-menu w-100 p-2"
                style={{ maxHeight: "280px", overflowY: "auto" }}
              >
                {/* 🔍 Search inside dropdown */}
                <li className="mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search category..."
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  />
                </li>

                <li><hr className="dropdown-divider" /></li>

                {/* Category List */}
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((item) => (
                    <li key={item.id}>
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          setSelectedCategory(item.catagories);
                          setCategoryFilter("");
                        }}
                      >
                        {item.catagories}
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="dropdown-item text-muted text-center">
                    No category found
                  </li>
                )}
              </ul>
            </div>

            {/* SEARCH BUTTON */}
            <button className="fb btn btn-primary w-50 w-lg-auto" onClick={goToSearch}>
              Search
            </button>

          </div>
        </div>
      </section >
    </>
  );
}
